/**
 * Odoo Explorer · Módulo admin para navegar la BD Odoo (solo lectura)
 * ═══════════════════════════════════════════════════════════════════
 * Página que consume /v1/odoo/* y permite ver en vivo las 8 tablas
 * maestras entregadas por TresCloud. Sirve para:
 *   • Validar que la integración funciona antes de escribir código de sync
 *   • Debug rápido cuando algo no cuadra con Odoo
 *   • Presentar al cliente que "sí tenemos acceso a sus datos"
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Database, RefreshCw, Loader2, Search, Circle, ChevronRight,
  Building2, Users, Package, ClipboardList, FileText, Boxes, Tag, HardDrive,
  AlertTriangle, CheckCircle2, Zap, Play, Settings2, Stethoscope, X, Copy,
} from "lucide-react";

const RESOURCES = [
  { key: "branches",        label: "Sucursales",       icon: Building2,    path: "/v1/odoo/branches",           searchable: false, note: "Q2 · disponible" },
  { key: "users",           label: "Usuarios Odoo",    icon: Users,        path: "/v1/odoo/users",              searchable: true,  note: "Q27 · JOIN habilitado" },
  { key: "partners",        label: "Contactos",        icon: Users,        path: "/v1/odoo/partners",           searchable: true,  note: "res_partner" },
  { key: "tasks",           label: "Órdenes (tasks)",  icon: ClipboardList, path: "/v1/odoo/project-tasks",     searchable: true,  note: "Q1/Q2 · 11 cols" },
  { key: "subscriptions",   label: "Suscripciones",    icon: FileText,     path: "/v1/odoo/subscriptions",      searchable: false, note: "sale_subscription" },
  { key: "stock_locations", label: "Bodegas",          icon: Boxes,        path: "/v1/odoo/stock/locations",    searchable: true,  note: "Q17 · category_id pendiente" },
  { key: "stock_quants",    label: "Existencias",      icon: Package,      path: "/v1/odoo/stock/quants",       searchable: false, note: "Q19 · reserved separado" },
  { key: "stock_lots",      label: "Series (lotes)",   icon: HardDrive,    path: "/v1/odoo/stock/lots",         searchable: true,  note: "Q18 · usar name" },
];

export default function OdooExplorer() {
  const [tab, setTab] = useState("explorer"); // explorer | sync
  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(true);
  const [selected, setSelected] = useState("branches");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // v2.9.7 · Diagnóstico
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [diagError, setDiagError] = useState(null);

  const current = RESOURCES.find((r) => r.key === selected);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadHealth = async () => {
    setCheckingHealth(true);
    try {
      const { data } = await api.get("/v1/odoo/health");
      setHealth(data);
    } catch (e) {
      setHealth({ enabled: false, connected: false, error: e?.message });
    } finally {
      setCheckingHealth(false);
    }
  };

  const loadResource = async () => {
    if (!current) return;
    setLoading(true);
    try {
      // v2.9.1 · Si es tasks y el usuario tecleó un ID numérico, consulta directa
      // a /v1/odoo/project-tasks/{id} (Odoo Ref lookup)
      if (current.key === "tasks" && debouncedSearch && /^\d+$/.test(debouncedSearch.trim())) {
        try {
          const { data } = await api.get(`/v1/odoo/project-tasks/${debouncedSearch.trim()}`);
          // Endpoint devuelve la fila directamente, envolvemos como items[]
          const item = data?.item || data;
          setItems(item && item.id ? [item] : []);
          return;
        } catch (idErr) {
          if (idErr?.response?.status === 404) {
            setItems([]);
            toast.info(`OT ${debouncedSearch} no existe en Odoo`);
            return;
          }
          // fallback a búsqueda normal si el endpoint falla
        }
      }
      const params = { limit: 25 };
      if (current.searchable && debouncedSearch) params.search = debouncedSearch;
      const { data } = await api.get(current.path, { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(`Error consultando ${current.label}: ${e?.response?.data?.detail || e.message}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHealth(); }, []);
  useEffect(() => { setSearch(""); setDebouncedSearch(""); setItems([]); }, [selected]);
  useEffect(() => { if (health?.connected) loadResource(); }, [selected, debouncedSearch, health?.connected]);

  // v2.9.7 · Diagnóstico Odoo (5 checks granulares)
  const runDiagnose = async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagError(null);
    setDiagResult(null);
    try {
      const { data } = await api.get("/v1/odoo/diagnose");
      setDiagResult(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setDiagError("El endpoint no existe en este ambiente. Haz REDEPLOY para publicarlo.");
      } else if (status === 401 || status === 403) {
        setDiagError("No autorizado. Inicia sesión con un usuario admin.");
      } else {
        setDiagError(e?.response?.data?.detail || e.message || "Error desconocido");
      }
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <Layout
      title="Odoo Explorer"
      subtitle="Navegación en vivo de la BD Odoo · TresCloud (base de pruebas)"
      wide
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={runDiagnose}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded transition"
            style={{ background: "var(--secondary)", color: "#fff", border: "1px solid var(--secondary)" }}
            data-testid="odoo-diagnose-btn"
          >
            <Stethoscope size={14} />
            Diagnosticar
          </button>
          <button onClick={loadHealth} className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50" data-testid="odoo-refresh-health">
            <RefreshCw size={14} />
          </button>
        </div>
      }
    >
      {/* Health banner */}
      <HealthBanner health={health} checking={checkingHealth} />

      {/* Tabs */}
      <div className="mt-3 flex gap-1 border-b border-gray-200">
        <TabButton active={tab === "explorer"} onClick={() => setTab("explorer")} icon={Database} label="Recursos" testid="tab-explorer" />
        <TabButton active={tab === "free"} onClick={() => setTab("free")} icon={Search} label="Explorador Libre" testid="tab-free" />
        <TabButton active={tab === "sync"} onClick={() => setTab("sync")} icon={Zap} label="Sincronización" testid="tab-sync" />
      </div>

      {tab === "sync" ? (
        <SyncPanel />
      ) : tab === "free" ? (
        <FreeExplorer />
      ) : (
      <div className="flex gap-3 mt-3" style={{ minHeight: "calc(100vh - 380px)" }}>
        {/* Sidebar: resources */}
        <div className="w-64 neu-card overflow-hidden">
          <div className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#6B7000" }}>
              Recursos ({RESOURCES.length})
            </div>
          </div>
          <div className="p-2 space-y-1">
            {RESOURCES.map((r) => {
              const Icon = r.icon;
              const active = selected === r.key;
              return (
                <button
                  key={r.key}
                  onClick={() => setSelected(r.key)}
                  className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 transition"
                  style={{
                    background: active ? "linear-gradient(145deg, #EDF34C, #C4CA12)" : "transparent",
                    boxShadow: active
                      ? "-3px -3px 8px rgba(255,255,255,0.8), 3px 3px 8px rgba(163,168,33,0.4)"
                      : "none",
                    color: active ? "#3F2E00" : "#0A3747",
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(10,55,71,0.05)"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  data-testid={`odoo-resource-${r.key}`}
                >
                  <Icon size={14} style={{ color: active ? "#3F2E00" : "#0A3747" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">{r.label}</div>
                    <div className="text-[9px] font-mono opacity-70 truncate">{r.note}</div>
                  </div>
                  <ChevronRight size={12} style={{ opacity: active ? 1 : 0.3 }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 neu-card overflow-hidden flex flex-col">
          <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2">
              {current && <current.icon size={16} style={{ color: "#0A3747" }} />}
              <div className="text-sm font-bold" style={{ color: "#0A3747" }}>{current?.label}</div>
              <span className="neu-chip text-[10px] font-mono" style={{ color: "#64748B" }}>GET {current?.path}</span>
            </div>
            {current?.searchable && (
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={current.key === "tasks" ? "Buscar (texto o Odoo Ref ID)..." : "Buscar..."}
                  className="neu-input pl-8"
                  style={{ padding: "8px 12px 8px 32px", fontSize: 12, width: 280 }}
                  data-testid="odoo-search"
                />
              </div>
            )}
          </div>

          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="py-20 flex justify-center">
                <Loader2 className="animate-spin text-gray-400" />
              </div>
            ) : items.length === 0 ? (
              <div className="py-20 text-center text-sm text-gray-400 italic">
                {health?.connected ? "Sin resultados" : "Conexión Odoo no disponible"}
              </div>
            ) : (
              <DataTable items={items} />
            )}
          </div>

          <div className="px-3 py-2 border-t bg-gray-50 flex items-center justify-between text-[10px] font-mono text-gray-500">
            <div>{items.length} filas mostradas (top 25)</div>
            <div>Datos en vivo desde Odoo · lectura</div>
          </div>
        </div>
      </div>
      )}

      {/* v2.9.7 · Modal de Diagnóstico Odoo PG (5 checks granulares) */}
      {diagOpen && (
        <DiagnoseModal
          open={diagOpen}
          onClose={() => setDiagOpen(false)}
          loading={diagLoading}
          error={diagError}
          result={diagResult}
          onRetry={runDiagnose}
        />
      )}
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Modal de Diagnóstico Odoo · v2.9.7
// ═══════════════════════════════════════════════════════════════════
function DiagnoseModal({ open, onClose, loading, error, result, onRetry }) {
  if (!open) return null;

  const STEP_LABEL = {
    env: "Variables de entorno",
    dns: "Resolución DNS",
    tcp: "Handshake TCP",
    sql: "Consulta SQL",
    egress_ip: "IP pública saliente",
  };
  const STEP_DESC = {
    env: "ODOO_PG_ENABLED y ODOO_PG_URL configuradas en el pod",
    dns: "El pod resuelve el hostname del servidor Odoo",
    tcp: "El puerto 5432 acepta conexión desde este pod (firewall)",
    sql: "El usuario alfafield puede ejecutar SELECT",
    egress_ip: "IP que este pod muestra al mundo · pídela para whitelist",
  };
  const VERDICT_META = {
    ok: { color: "#16A34A", bg: "#DCFCE7", label: "TODO OK", icon: CheckCircle2 },
    env_missing: { color: "#DC2626", bg: "#FEE2E2", label: "Variables faltantes", icon: AlertTriangle },
    dns_fail: { color: "#DC2626", bg: "#FEE2E2", label: "Fallo DNS", icon: AlertTriangle },
    tcp_timeout: { color: "#F59E0B", bg: "#FEF3C7", label: "Firewall del cliente", icon: AlertTriangle },
    tcp_refused: { color: "#DC2626", bg: "#FEE2E2", label: "Puerto rechaza", icon: AlertTriangle },
    sql_fail: { color: "#DC2626", bg: "#FEE2E2", label: "Fallo SQL", icon: AlertTriangle },
    engine_missing: { color: "#DC2626", bg: "#FEE2E2", label: "Engine no inicializado", icon: AlertTriangle },
    unknown: { color: "#94A3B8", bg: "#F1F5F9", label: "Sin verdict", icon: Circle },
  };
  const meta = result ? (VERDICT_META[result.verdict] || VERDICT_META.unknown) : VERDICT_META.unknown;
  const VerdictIcon = meta.icon;

  const copyEgress = () => {
    const ip = result?.steps?.find((s) => s.step === "egress_ip")?.public_ip;
    if (ip) {
      navigator.clipboard?.writeText(ip);
      toast.success(`IP ${ip} copiada al portapapeles`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="diagnose-modal"
    >
      <div
        className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ background: "linear-gradient(135deg,#0A3747,#135166)", color: "#fff" }}>
          <div className="flex items-center gap-2">
            <Stethoscope size={16} />
            <div>
              <div className="text-xs font-mono uppercase tracking-widest opacity-70">Diagnóstico</div>
              <div className="text-sm font-bold">Conexión Odoo PG</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="diagnose-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-center py-10">
              <Loader2 size={28} className="animate-spin mx-auto" style={{ color: "var(--secondary)" }} />
              <div className="mt-3 text-sm font-mono uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                Ejecutando 5 checks…
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg p-4" style={{ background: "#FEE2E2", border: "1px solid #FCA5A5" }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-red-600" />
                <div className="font-bold text-red-800">No se pudo ejecutar el diagnóstico</div>
              </div>
              <div className="text-sm text-red-700 mb-3">{error}</div>
              {/^El endpoint/.test(error) && (
                <div className="text-xs bg-white rounded p-3 mt-2" style={{ border: "1px solid #FCA5A5" }}>
                  <div className="font-bold mb-1">Cómo publicarlo:</div>
                  1. En Emergent ve a <span className="font-mono bg-gray-100 px-1">Deploy</span><br />
                  2. Haz <span className="font-bold">Redeploy</span> del backend<br />
                  3. Espera ~2 min a que termine y vuelve a pulsar &quot;Diagnosticar&quot;
                </div>
              )}
              <button onClick={onRetry} className="mt-3 px-3 py-1.5 text-xs font-semibold rounded" style={{ background: "#0A3747", color: "#fff" }}>
                Reintentar
              </button>
            </div>
          )}

          {result && !loading && !error && (
            <>
              {/* Verdict global */}
              <div className="rounded-lg p-4" style={{ background: meta.bg, border: `1px solid ${meta.color}` }}>
                <div className="flex items-center gap-3">
                  <VerdictIcon size={26} style={{ color: meta.color }} />
                  <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: meta.color }}>Veredicto</div>
                    <div className="text-lg font-black" style={{ color: meta.color }} data-testid="diagnose-verdict">
                      {meta.label}
                    </div>
                  </div>
                </div>
                {result.recommendation && (
                  <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-main)" }}>
                    {result.recommendation}
                  </div>
                )}
              </div>

              {/* Steps */}
              <div className="space-y-2">
                {result.steps?.map((s) => {
                  const ok = s.ok;
                  const bg = ok ? "#DCFCE7" : "#FEE2E2";
                  const border = ok ? "#86EFAC" : "#FCA5A5";
                  const iconColor = ok ? "#16A34A" : "#DC2626";
                  const Icon = ok ? CheckCircle2 : AlertTriangle;
                  return (
                    <div key={s.step} className="rounded-lg p-3" style={{ background: bg, border: `1px solid ${border}` }} data-testid={`diagnose-step-${s.step}`}>
                      <div className="flex items-start gap-3">
                        <Icon size={18} style={{ color: iconColor, marginTop: 2 }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="text-sm font-bold" style={{ color: "var(--text-main)" }}>
                              {STEP_LABEL[s.step] || s.step}
                            </div>
                            {s.ms != null && (
                              <div className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>
                                {s.ms} ms
                              </div>
                            )}
                          </div>
                          <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                            {STEP_DESC[s.step]}
                          </div>
                          {/* Detalles clave por paso */}
                          <div className="mt-1.5 text-xs font-mono space-y-0.5" style={{ color: "var(--text-main)" }}>
                            {s.step === "env" && s.ok && (
                              <>
                                <div>host: <b>{s.host}:{s.port}</b></div>
                                <div>user: <b>{s.user}</b> · db: <b>{s.database}</b></div>
                              </>
                            )}
                            {s.step === "dns" && s.ok && <div>resolved: <b>{s.resolved_ip}</b></div>}
                            {s.step === "tcp" && s.ok && <div>{s.target} ✅</div>}
                            {s.step === "sql" && s.ok && <div>{(s.version || "").slice(0, 60)}…</div>}
                            {s.step === "egress_ip" && s.ok && (
                              <div className="flex items-center gap-2">
                                <span>public_ip: <b className="text-base">{s.public_ip}</b></span>
                                <button onClick={copyEgress} className="p-1 rounded hover:bg-white/60" title="Copiar IP" data-testid="diagnose-copy-egress">
                                  <Copy size={12} />
                                </button>
                              </div>
                            )}
                            {s.error && <div className="text-red-700">error: {s.error}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Guía de acción según verdict */}
              {result.verdict === "tcp_timeout" && (() => {
                const envStep = result.steps?.find((s) => s.step === "env") || {};
                const egressStep = result.steps?.find((s) => s.step === "egress_ip") || {};
                const hostPort = envStep.host ? `${envStep.host}:${envStep.port || 5432}` : "(host Odoo)";
                const publicIp = egressStep.public_ip || "(ver check IP pública saliente)";
                return (
                  <div className="rounded-lg p-4" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
                    <div className="font-bold mb-2 flex items-center gap-2" style={{ color: "#92400E" }}>
                      <AlertTriangle size={16} />
                      Acción requerida · Whitelist en TresCloud
                    </div>
                    <div className="text-sm mb-3" style={{ color: "#78350F" }}>
                      La IP pública saliente de este pod NO tiene acceso al servidor Odoo. Envíale este mensaje al cliente:
                    </div>
                    <div className="rounded p-3 mb-3 font-mono text-xs bg-white leading-relaxed" style={{ border: "1px solid #F59E0B", color: "#1F2937" }}>
                      Hola equipo TresCloud, necesitamos que autoricen la IP <b>{publicIp}</b> en el firewall del servidor Odoo (<b>{hostPort}</b>). Corresponde al pod de producción de Alfa-Field Connect (usuario <code>{envStep.user || "alfafield"}</code>, base <code>{envStep.database || "trescloud-alfanet-15-0-4417468"}</code>). Actualmente el TCP handshake agota timeout desde nuestro entorno.
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const msg = `Hola equipo TresCloud, necesitamos que autoricen la IP ${publicIp} en el firewall del servidor Odoo (${hostPort}). Corresponde al pod de producción de Alfa-Field Connect (usuario ${envStep.user || "alfafield"}, base ${envStep.database || "trescloud-alfanet-15-0-4417468"}). Actualmente el TCP handshake agota timeout desde nuestro entorno.`;
                          navigator.clipboard?.writeText(msg);
                          toast.success("Mensaje copiado — pégalo en tu correo/chat con TresCloud");
                        }}
                        className="px-3 py-1.5 text-xs font-semibold rounded"
                        style={{ background: "#0A3747", color: "#fff" }}
                        data-testid="diagnose-copy-message"
                      >
                        <Copy size={11} className="inline mr-1" />
                        Copiar mensaje completo
                      </button>
                      <button
                        onClick={() => {
                          if (publicIp && publicIp !== "(ver check IP pública saliente)") {
                            navigator.clipboard?.writeText(publicIp);
                            toast.success(`IP ${publicIp} copiada`);
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-semibold rounded"
                        style={{ background: "#fff", color: "#0A3747", border: "1px solid #0A3747" }}
                      >
                        <Copy size={11} className="inline mr-1" />
                        Solo la IP
                      </button>
                    </div>
                    <div className="text-[11px] mt-3" style={{ color: "#78350F" }}>
                      Cuando ellos apliquen la regla (5-10 min), pulsa <b>Volver a ejecutar</b>.
                    </div>
                  </div>
                );
              })()}
              {result.verdict === "env_missing" && (
                <div className="rounded-lg p-4" style={{ background: "#FEE2E2", border: "1px solid #DC2626" }}>
                  <div className="font-bold mb-2" style={{ color: "#991B1B" }}>Acción · Configurar variables de entorno</div>
                  <div className="text-sm" style={{ color: "#7F1D1D" }}>
                    En Emergent ve a <b>Deploy → Environment Variables</b> del backend y añade:<br />
                    <div className="mt-2 font-mono text-xs bg-white rounded p-2" style={{ border: "1px solid #FCA5A5" }}>
                      ODOO_PG_ENABLED=true<br />
                      ODOO_PG_URL=postgresql+asyncpg://alfafield_test:PASSWORD@200.25.39.81:5432/trescloud-alfanet-15-0-4417468
                    </div>
                    Luego haz <b>Redeploy</b> del backend.
                  </div>
                </div>
              )}
              {result.verdict === "sql_fail" && (
                <div className="rounded-lg p-4" style={{ background: "#FEE2E2", border: "1px solid #DC2626" }}>
                  <div className="font-bold mb-1" style={{ color: "#991B1B" }}>Acción · Revisar credenciales</div>
                  <div className="text-sm" style={{ color: "#7F1D1D" }}>
                    TCP funciona pero la autenticación o query SQL falla. Verifica en el .env de producción que el <b>usuario y password</b> de <code>ODOO_PG_URL</code> sean correctos.
                  </div>
                </div>
              )}

              <button
                onClick={onRetry}
                className="w-full py-2 text-sm font-semibold rounded-lg"
                style={{ background: "var(--secondary)", color: "#fff" }}
                data-testid="diagnose-retry"
              >
                <RefreshCw size={14} className="inline mr-1" />
                Volver a ejecutar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v7.3.0 · Explorador Libre — cualquier tabla de Odoo
// ═══════════════════════════════════════════════════════════════════
function FreeExplorer() {
  const [tables, setTables] = useState([]);
  const [tableFilter, setTableFilter] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [loadingTables, setLoadingTables] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Load tables list
  useEffect(() => {
    setLoadingTables(true);
    api.get("/v1/odoo/tables")
      .then((r) => setTables(r.data.items || []))
      .catch((e) => toast.error("Error cargando tablas: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoadingTables(false));
  }, []);

  // Load table data
  useEffect(() => {
    if (!selectedTable) return;
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (debouncedSearch) params.q = debouncedSearch;
    api.get(`/v1/odoo/explore/${selectedTable}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(`Error: ${e?.response?.data?.detail || e.message}`);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [selectedTable, debouncedSearch, page]);

  const filteredTables = tables.filter((t) =>
    !tableFilter || t.toLowerCase().includes(tableFilter.toLowerCase())
  );

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="flex gap-3 mt-3" style={{ minHeight: "calc(100vh - 380px)" }}>
      {/* Sidebar: table selector */}
      <div className="w-72 neu-card overflow-hidden flex flex-col">
        <div className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#6B7000" }}>
            Tablas Odoo ({tables.length})
          </div>
          <div className="mt-2 relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <input
              type="text"
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              placeholder="Filtrar tablas..."
              className="neu-input w-full"
              style={{ padding: "6px 10px 6px 28px", fontSize: 11 }}
              data-testid="free-table-filter"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-1.5" style={{ maxHeight: "calc(100vh - 480px)" }}>
          {loadingTables ? (
            <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
          ) : filteredTables.length === 0 ? (
            <div className="py-6 text-center text-[10px] text-gray-400">Sin resultados</div>
          ) : (
            filteredTables.map((t) => {
              const active = selectedTable === t;
              const isHighlight = ["service_activity_type", "project_task", "res_partner", "stock_quant", "res_users"].includes(t);
              return (
                <button
                  key={t}
                  onClick={() => { setSelectedTable(t); setSearch(""); setPage(0); setData(null); }}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition text-xs"
                  style={{
                    background: active ? "linear-gradient(145deg, #EDF34C, #C4CA12)" : isHighlight ? "rgba(224,230,33,0.08)" : "transparent",
                    boxShadow: active ? "-2px -2px 6px rgba(255,255,255,0.8), 2px 2px 6px rgba(163,168,33,0.3)" : "none",
                    fontWeight: active ? 800 : isHighlight ? 600 : 400,
                    color: active ? "#3F2E00" : "#0A3747",
                    borderLeft: isHighlight && !active ? "3px solid #E0E621" : "3px solid transparent",
                  }}
                  data-testid={`free-table-${t}`}
                >
                  <Database size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
                  <span className="truncate font-mono">{t}</span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 neu-card overflow-hidden flex flex-col">
        {!selectedTable ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Database size={48} className="mx-auto mb-3 opacity-20" />
              <div className="text-sm font-bold text-gray-400">Selecciona una tabla</div>
              <div className="text-xs text-gray-400 mt-1">Explora cualquier tabla de la BD Odoo en tiempo real</div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {["service_activity_type", "project_task", "res_partner", "stock_quant", "res_users"].map((t) => (
                  <button
                    key={t}
                    onClick={() => { setSelectedTable(t); setSearch(""); setPage(0); setData(null); }}
                    className="px-3 py-1.5 text-[10px] font-mono font-bold rounded-full"
                    style={{ background: "#0A3747", color: "#E0E621" }}
                    data-testid={`free-quick-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="flex items-center gap-2">
                <Database size={16} style={{ color: "#0A3747" }} />
                <div className="text-sm font-bold font-mono" style={{ color: "#0A3747" }}>{selectedTable}</div>
                {data && (
                  <span className="neu-chip text-[10px] font-mono" style={{ color: "#64748B" }}>
                    {data.total.toLocaleString("es-EC")} registros · {data.columns?.length} columnas
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por ID o nombre..."
                    className="neu-input pl-8"
                    style={{ padding: "8px 12px 8px 32px", fontSize: 12, width: 280 }}
                    data-testid="free-search"
                  />
                </div>
                <button
                  onClick={() => { setData(null); setSearch(""); setPage(0); const t = selectedTable; setSelectedTable(""); setTimeout(() => setSelectedTable(t), 50); }}
                  className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
                  title="Refrescar"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>

            {/* Column chips */}
            {data?.columns && (
              <div className="px-4 py-2 flex flex-wrap gap-1 border-b border-gray-100 bg-gray-50/50" style={{ maxHeight: 60, overflowY: "auto" }}>
                {data.columns.map((c) => (
                  <span key={c.name} className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: c.name === "id" || c.name === "name" ? "#FEF9C3" : "#F1F5F9", color: "#475569" }}>
                    {c.name} <span style={{ color: "#94A3B8" }}>({c.type})</span>
                  </span>
                ))}
              </div>
            )}

            {/* Data grid */}
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
              ) : !data || data.items.length === 0 ? (
                <div className="py-20 text-center text-sm text-gray-400 italic">Sin resultados</div>
              ) : (
                <DataTable items={data.items} />
              )}
            </div>

            {/* Footer with pagination */}
            <div className="px-3 py-2 border-t bg-gray-50 flex items-center justify-between text-[10px] font-mono text-gray-500">
              <div>
                {data ? `Mostrando ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, data.total)} de ${data.total.toLocaleString("es-EC")}` : "—"}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-30 text-[10px]"
                  data-testid="free-prev"
                >
                  ← Anterior
                </button>
                <span className="px-2">{page + 1} / {totalPages || 1}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-30 text-[10px]"
                  data-testid="free-next"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 transition ${
        active ? "border-[#E0E621] text-[#0A3747]" : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
      data-testid={testid}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function SyncPanel() {
  const [config, setConfig] = useState([]);
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r, s] = await Promise.all([
        api.get("/v1/odoo/sync/config"),
        api.get("/v1/odoo/sync/runs", { params: { limit: 15 } }),
        api.get("/v1/odoo/sync/stats"),
      ]);
      setConfig(c.data.items || []);
      setRuns(r.data.items || []);
      setStats(s.data.mirror_row_counts || {});
    } catch (e) {
      toast.error("Error cargando sync config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const patch = async (key, body) => {
    try {
      await api.patch(`/v1/odoo/sync/config/${key}`, body);
      toast.success("Configuración actualizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const trigger = async (key) => {
    setRunning(key);
    try {
      await api.post(`/v1/odoo/sync/run/${key}`);
      toast.success(`Sync de ${key} iniciado en background`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setTimeout(() => setRunning(null), 3000);
    }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;

  return (
    <div className="mt-3 space-y-3">
      {/* Config cards */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-[#0A3747] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings2 size={14} />
            <div className="text-[11px] font-mono uppercase tracking-widest">Recursos configurados</div>
          </div>
          <button onClick={load} className="text-[10px] font-mono opacity-80 hover:opacity-100" data-testid="sync-refresh">
            <RefreshCw size={12} className="inline" /> Refrescar
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {config.map((c) => (
            <SyncRow key={c.resource_key} cfg={c} onPatch={patch} onTrigger={trigger} running={running === c.resource_key} rowCount={stats[`odoo_mirror_${c.resource_key}`] || 0} />
          ))}
        </div>
      </div>

      {/* Historial */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-2">
          <RefreshCw size={12} className="text-gray-500" />
          <div className="text-[11px] font-mono uppercase tracking-widest text-gray-700">Historial de ejecuciones (últimas 15)</div>
        </div>
        {runs.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400 italic">Sin ejecuciones aún</div>
        ) : (
          <table className="w-full text-xs" data-testid="sync-runs-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase">Recurso</th>
                <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase">Iniciado</th>
                <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase">Duración</th>
                <th className="px-2 py-1.5 text-right font-mono text-[9px] uppercase">Fetch</th>
                <th className="px-2 py-1.5 text-right font-mono text-[9px] uppercase">Upsert</th>
                <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase">Estado</th>
                <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const dur = r.finished_at && r.started_at
                  ? Math.round((new Date(r.finished_at) - new Date(r.started_at)) / 1000)
                  : null;
                const badge = {
                  success: "bg-emerald-100 text-emerald-800",
                  error: "bg-red-100 text-red-800",
                  running: "bg-blue-100 text-blue-800 animate-pulse",
                }[r.status] || "bg-gray-100 text-gray-700";
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.resource_key}</td>
                    <td className="px-2 py-1.5 text-[10px] text-gray-600">{new Date(r.started_at).toLocaleString("es-EC")}</td>
                    <td className="px-2 py-1.5 text-[10px] text-gray-600">{dur !== null ? `${dur}s` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.rows_fetched}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{r.rows_upserted}</td>
                    <td className="px-2 py-1.5"><span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${badge}`}>{r.status}</span></td>
                    <td className="px-2 py-1.5 text-[9px] text-gray-500">{r.triggered_by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SyncRow({ cfg, onPatch, onTrigger, running, rowCount }) {
  const [interval_min, setInterval_min] = useState(cfg.interval_min);
  const [batch, setBatch] = useState(cfg.batch_size);
  const statusColor = {
    success: "bg-emerald-500",
    error: "bg-red-500",
    running: "bg-blue-500 animate-pulse",
    idle: "bg-gray-300",
  }[cfg.last_status] || "bg-gray-300";
  return (
    <div className="px-3 py-3 flex items-center gap-4 flex-wrap" data-testid={`sync-row-${cfg.resource_key}`}>
      <div className="flex items-center gap-2 w-48">
        <span className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
        <div>
          <div className="text-sm font-bold text-[#0A3747]">{cfg.label}</div>
          <div className="text-[10px] font-mono text-gray-500">{cfg.resource_key}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => onPatch(cfg.resource_key, { enabled: e.target.checked })}
          className="accent-[#0A3747] w-4 h-4"
          data-testid={`sync-enabled-${cfg.resource_key}`}
        />
        <label className="text-xs text-gray-700">Auto</label>
      </div>
      <div className="flex items-center gap-1 text-xs">
        <label className="text-gray-500">Cada</label>
        <input
          type="number" min={1} max={1440}
          value={interval_min}
          onChange={(e) => setInterval_min(Number(e.target.value))}
          onBlur={() => interval_min !== cfg.interval_min && onPatch(cfg.resource_key, { interval_min })}
          className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
          data-testid={`sync-interval-${cfg.resource_key}`}
        />
        <span className="text-gray-500">min</span>
      </div>
      <div className="flex items-center gap-1 text-xs">
        <label className="text-gray-500">Batch</label>
        <input
          type="number" min={10} max={2000}
          value={batch}
          onChange={(e) => setBatch(Number(e.target.value))}
          onBlur={() => batch !== cfg.batch_size && onPatch(cfg.resource_key, { batch_size: batch })}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
          data-testid={`sync-batch-${cfg.resource_key}`}
        />
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="text-[10px] font-mono text-gray-500">
          Último: {cfg.last_run_at ? new Date(cfg.last_run_at).toLocaleString("es-EC") : "nunca"} · {cfg.last_status}
        </div>
        {cfg.last_error && (
          <div className="text-[10px] mt-1" title={cfg.last_error}>
            {(() => {
              const err = cfg.last_error || "";
              const isPriv = /InsufficientPrivilege|permission denied|no permiso/i.test(err);
              if (isPriv) {
                // Try to extract table name
                const m = err.match(/relation ["']?([\w.]+)["']?/i) || err.match(/table ["']?([\w.]+)["']?/i);
                const tbl = m ? m[1] : cfg.resource_key;
                return (
                  <div className="flex items-start gap-1.5 p-2 rounded-md" style={{ background: "rgba(217, 119, 6, 0.08)", border: "1px solid rgba(217, 119, 6, 0.25)" }}>
                    <span className="text-amber-600 font-bold">⚠</span>
                    <div>
                      <div className="text-amber-700 font-semibold text-[10px] uppercase tracking-wider">Sin permisos en Odoo</div>
                      <div className="text-amber-800 text-[10px] mt-0.5">
                        El usuario Odoo no tiene <b>SELECT</b> sobre <code className="font-mono">{tbl}</code>. Pide al DBA de Odoo:
                        <br/>
                        <code className="font-mono text-[9.5px] bg-white/60 px-1 py-0.5 rounded mt-0.5 inline-block">GRANT SELECT ON {tbl} TO {'{usuario}'};</code>
                      </div>
                    </div>
                  </div>
                );
              }
              return <div className="text-[9px] text-red-700 truncate">⚠ {err}</div>;
            })()}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-600 whitespace-nowrap">
        <span className="font-bold text-[#0A3747]">{rowCount.toLocaleString("es-EC")}</span> locales<br/>
        <span className="text-[9px] font-mono text-gray-400">+{cfg.rows_synced?.toLocaleString("es-EC") || 0} acum.</span>
      </div>
      <button
        onClick={() => onTrigger(cfg.resource_key)}
        disabled={running || cfg.last_status === "running"}
        className="px-3 py-1.5 rounded bg-[#0A3747] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#135166] disabled:opacity-50"
        data-testid={`sync-run-${cfg.resource_key}`}
      >
        {(running || cfg.last_status === "running") ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
        Ejecutar ahora
      </button>
    </div>
  );
}

function HealthBanner({ health, checking }) {
  if (checking) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="animate-spin" size={14} /> Verificando conexión con Odoo...
      </div>
    );
  }
  const ok = health?.connected;
  return (
    <div className={`border rounded-lg p-3 flex items-start gap-3 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="odoo-health-banner">
      {ok ? (
        <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
      ) : (
        <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 text-xs">
        {ok ? (
          <>
            <div className="font-bold text-emerald-900">Conectado a Odoo</div>
            <div className="text-emerald-800 mt-0.5">
              <span className="font-mono">DB:</span> {health.database} ·
              <span className="font-mono ml-2">Usuario:</span> {health.user} ·
              <span className="font-mono ml-2">Motor:</span> {(health.version || "").split(",")[0]}
            </div>
          </>
        ) : (
          <>
            <div className="font-bold text-red-900">Sin conexión a Odoo</div>
            <div className="text-red-800 mt-0.5">
              {health?.error || health?.reason || "Verifica ODOO_PG_URL en el .env del backend."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DataTable({ items }) {
  if (!items.length) return null;
  const allColumns = Object.keys(items[0]);
  // v2.9.9 · Columnas de visibilidad prioritaria (pinned al inicio + highlight)
  const PRIORITY_COLS = ["id", "stage_id", "fsm_done", "write_date"];
  const priority = PRIORITY_COLS.filter((c) => allColumns.includes(c));
  const rest = allColumns.filter((c) => !PRIORITY_COLS.includes(c));
  const columns = [...priority, ...rest];
  const isPriority = (c) => PRIORITY_COLS.includes(c);
  return (
    <table className="w-full text-xs neu-table" data-testid="odoo-data-table">
      <thead className="sticky top-0 z-10">
        <tr>
          {columns.map((c) => (
            <th
              key={c}
              className="px-2 py-1.5 text-left whitespace-nowrap"
              style={isPriority(c) ? { background: "#FEF9C3", color: "#713F12", fontWeight: 800 } : undefined}
              data-testid={`odoo-col-${c}`}
            >
              {c}
              {isPriority(c) && <span className="ml-1 text-[8px] text-amber-600">★</span>}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={i} className={i % 2 ? "bg-gray-50" : ""}>
            {columns.map((c) => (
              <td
                key={c}
                className="px-2 py-1 border-t border-gray-100 whitespace-nowrap max-w-xs truncate"
                style={isPriority(c) ? { background: "#FEFCE8", fontFamily: "ui-monospace, monospace", fontWeight: 600 } : undefined}
              >
                {renderCell(c, it[c])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// v2.9.9 · Render específico para columnas clave (badges de estado)
function renderCell(col, v) {
  if (col === "fsm_done") {
    if (v === true) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#FEE2E2", color: "#991B1B" }}>true (zombi)</span>;
    if (v === false) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#DCFCE7", color: "#166534" }}>false</span>;
    return <span className="text-gray-300">—</span>;
  }
  if (col === "stage_id") {
    if (v == null) return <span className="text-gray-300">—</span>;
    const isWhitelisted = Number(v) === 22;
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: isWhitelisted ? "#DCFCE7" : "#FEE2E2", color: isWhitelisted ? "#166534" : "#991B1B" }}>
        {v}{isWhitelisted ? " ✓" : " ✗"}
      </span>
    );
  }
  return renderValue(v);
}

function renderValue(v) {
  if (v === null || v === undefined) return <span className="text-gray-300">—</span>;
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (typeof v === "object") return <code className="text-[10px]">{JSON.stringify(v)}</code>;
  const s = String(v);
  if (s.length > 60) {
    return <span title={s}>{s.slice(0, 60)}…</span>;
  }
  return s;
}
